/* eslint-disable no-console */
import { useMemo, useRef, useState } from 'react';
import { HexColorInput } from 'react-colorful';

import { useDebounceCallback, useOnClickOutside } from 'usehooks-ts';

import { SingleSelect } from '@/common';
import { ColorPicker } from '@/components/generic/';
import { fonts } from '@/constants';
import { generateFontSizeOptions } from '@/helpers';
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  SolidCheckIcon as CheckIcon,
  SyncIcon
} from '@/resources';
import { HexColor } from '@/types';

import { AppliedStylesType } from './CreateEditCoverPhoto';
import { TestIds } from '@/shared/test-ids';

type TextEditorProps = {
  closeTextEditor: () => void;
  handleTextStyleChange: (value: string, property: string) => void;
  appliedStyles: AppliedStylesType;
  syncColors: (text: string) => void;
};

export const TextEditor = ({
  appliedStyles,
  closeTextEditor,
  handleTextStyleChange,
  syncColors
}: TextEditorProps) => {
  const [isColorPickerShown, setIsColorPickerShown] = useState<boolean>(false);
  const [isSyncShowing, setIsSyncShowing] = useState<boolean>(true);
  const textEditorRef = useRef(null);

  useOnClickOutside(textEditorRef, () => closeTextEditor());

  const fontSizeOptions = useMemo(() => generateFontSizeOptions(), []);

  const debounced = useDebounceCallback(
    (color: string) => handleTextStyleChange(color, 'fill'),
    500
  );

  const sync = async (text: string) => {
    syncColors(text);
    setIsSyncShowing(false);
  };

  return (
    <div
      data-testid={TestIds.coverPhotos.textEditor}
      className={'absolute left-0 top-20 z-50 w-full flex-col items-center bg-primary-white'}
      ref={textEditorRef}>
      <div className='w-full'>
        <div className='flex h-12 w-full items-center justify-between gap-x-2 rounded-[10px] border border-solid border-secondary-blue-gray px-2 shadow-lg'>
          <div className='flex gap-x-3'>
            <AlignLeftIcon
              data-testid={TestIds.coverPhotos.textEditorAlignLeft}
              onClick={() => handleTextStyleChange('left', 'textAlign')}
              className={`cursor-pointer ${appliedStyles.textAlign === 'left' ? 'opacity-100' : 'opacity-50'}`}
            />
            <AlignCenterIcon
              data-testid={TestIds.coverPhotos.textEditorAlignCenter}
              onClick={() => handleTextStyleChange('center', 'textAlign')}
              className={`cursor-pointer ${appliedStyles.textAlign === 'center' ? 'opacity-100' : 'opacity-50'}`}
            />
            <AlignRightIcon
              data-testid={TestIds.coverPhotos.textEditorAlignRight}
              onClick={() => handleTextStyleChange('right', 'textAlign')}
              className={`cursor-pointer ${appliedStyles.textAlign === 'right' ? 'opacity-100' : 'opacity-50'}`}
            />
          </div>
          <div className='flex gap-x-2'>
            <SingleSelect
              id='fonts'
              testId={TestIds.coverPhotos.textEditorFontSelect}
              options={fonts}
              handleChange={(value) => handleTextStyleChange(value?.value as string, 'fontFamily')}
              value={fonts.find((font) => font.value === appliedStyles.fontFamily)}
            />
            <SingleSelect
              id='fontSize'
              testId={TestIds.coverPhotos.textEditorFontSizeSelect}
              options={fontSizeOptions}
              handleChange={(value) => {
                handleTextStyleChange(value?.value as string, 'fontSize');
              }}
              value={fontSizeOptions.find((font) => font.value === appliedStyles.fontSize)}
            />
          </div>
          <div
            data-testid={TestIds.coverPhotos.textEditorColorPicker}
            style={{ backgroundColor: appliedStyles.fill }}
            className={'size-8 rounded-full border-2'}
            onClick={() => setIsColorPickerShown(!isColorPickerShown)}
          />
        </div>
        <div className='absolute bottom-[100px] right-0 md:-bottom-0 md:right-[-220px]'>
          <ColorPicker
            isOpen={isColorPickerShown}
            setIsOpen={setIsColorPickerShown}
            color={appliedStyles.fill as HexColor}
            onChange={(value) => debounced(value)}
            showColorPreview={false}
          />
          {isColorPickerShown && (
            <>
              <HexColorInput
                color={appliedStyles.fill}
                onChange={(value) => debounced(value)}
                className='relative w-full rounded-md border-2 py-1 text-center outline-none'
                data-prevent-click-outside={true}
              />
              {isSyncShowing ? (
                <SyncIcon
                  className='absolute bottom-0 right-0 m-2 cursor-pointer text-lg'
                  onClick={() => sync(appliedStyles.fill)}
                  data-prevent-click-outside={true}
                />
              ) : (
                <CheckIcon
                  className='absolute bottom-0 right-0 m-2 cursor-pointer text-lg'
                  data-prevent-click-outside={true}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
